import React, { memo } from 'react';
import styled from 'styled-components';
import { useManageCustomDetailsDefinitionsModalController } from './useManageCustomDetailsDefinitionsModalController.ts';
import { Button, Flex, Modal } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { HeaderCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import {
	CancelButton,
	SaveButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons.tsx';
import {
	BodyRow,
	HeaderRow,
	DefaultTable,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable.tsx';
import { AlertDescription, Alert } from '@dealstack/frontend/shared/components/Alert/Alert.tsx';
import { CustomDetailDefinitionRow } from './CustomDetailDefinitionRow/CustomDetailDefinitionRow.tsx';
import { Expandable } from '@dealstack/frontend/shared/components/Expandable/Expandable.tsx';

interface Props {
	className?: string;
	opened: boolean;
	onClose: () => void;
}

const ManageCustomDetailsDefinitionsModal = ({ opened, onClose, className }: Props) => {
	const {
		manageCustomDetailsDefinitionsForm,
		fields,
		onRemoveClicked,
		onAddNewClicked,
		onSubmit,
	} = useManageCustomDetailsDefinitionsModalController({
		onClose,
	});

	return (
		<Modal
			className={className}
			opened={opened}
			onClose={onClose}
			title="Manage custom details definitions for template"
			size="xl"
			centered
		>
			<FormProvider {...manageCustomDetailsDefinitionsForm}>
				<Expandable title="How to use it">
					<Flex direction="column" gap="1rem">
						<Alert state="info" size="m">
							<AlertDescription>
								You can request additional information from investors so it can be
								used to pre-fill the PDF form. To do this, create a custom detail
								definition by specifying a name (question) and optionally providing
								instructions. Once saved, the custom detail will appear in the
								available sources list within the edit field source modal.
							</AlertDescription>
						</Alert>

						<Alert state="warning" size="m">
							<AlertDescription>
								Subsequent effects will be applied to all MIPs using this template:
							</AlertDescription>
							<AlertDescription>
								1. Creating a definition will create custom details, prompting all
								investors in each affected MIP to respond during their next investor
								flow.
							</AlertDescription>
							<AlertDescription>
								2. Deleting a definition will invalidate any PDF fields using it,
								and they will require your attention to resolve.
							</AlertDescription>
						</Alert>
					</Flex>
				</Expandable>

				<DefaultTable maxSize withEditableRows>
					<HeaderRow>
						<HeaderCell customSize="1fr">Name</HeaderCell>
						<HeaderCell customSize="1fr">Instructions</HeaderCell>
						<HeaderCell size={2.1} />
					</HeaderRow>
					<BodyRow>
						{fields.map((field, index) => (
							<CustomDetailDefinitionRow
								key={field.localId}
								onRemoveClicked={() => {
									onRemoveClicked(index);
								}}
								index={index}
							/>
						))}
					</BodyRow>
				</DefaultTable>

				<Button
					variant="ghost"
					leftSection={
						<Icon variant="add-circle" size="lg" style={{ marginRight: '0.5rem' }} />
					}
					onClick={onAddNewClicked}
					mt="1rem"
				>
					Add new
				</Button>

				<Flex mt="2rem" justify="flex-end" gap="1rem">
					<CancelButton onClick={onClose} />

					<SaveButton
						onClick={manageCustomDetailsDefinitionsForm.handleSubmit(onSubmit)}
					/>
				</Flex>
			</FormProvider>
		</Modal>
	);
};

const Memoized = memo(ManageCustomDetailsDefinitionsModal);
const Styled = styled(Memoized)`
	${DefaultTable} {
		margin-top: 2rem;
	}
`;

export { Styled as ManageCustomDetailsDefinitionsModal };
